import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ApplicationInsights, IPageViewTelemetry, IEventTelemetry, IExceptionTelemetry, ITraceTelemetry, SeverityLevel, IDependencyListenerHandler } from "@microsoft/applicationinsights-web";
import { ConfigurationService } from "./configuration.service";
import { UserInfoService } from "./userinfo.service";
import packageJson from "../../../package.json";

@Injectable({
  providedIn: "root"
})
export class MonitoringService {
  private appInsights: ApplicationInsights;
  private initializationPromise: Promise<void>;
  private isInitialized: boolean;
  private traceId: string;
  private spanId: string;
  private handler: IDependencyListenerHandler;

  constructor(
    private readonly router: Router,
    private readonly configService: ConfigurationService,
    private readonly userInfo: UserInfoService) {
    this.initializationPromise = this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.configService.loadConfiguration();
    const appConfig = this.configService.config;
    if (appConfig.aiConnectionString) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: appConfig.aiConnectionString,
          enableAutoRouteTracking: true
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Manually call trackPageView to establish the current user/session/pageview
      this.appInsights.context.application.ver = packageJson.version;
      this.handler = this.appInsights.addDependencyListener((details) => {
        this.traceId = details.traceId;
        this.spanId = details.spanId;
      });
    }

    this.isInitialized = true;
  }

  getSessionId(): string {
    return this.appInsights?.context?.sessionManager?.automaticSession?.id;
  }

  getTraceAndSpanId(): string[] {
    this.handler?.remove();
    return [this.traceId, this.spanId];
  }

  getOperationId(): string {
    return this.appInsights?.getTraceCtx()?.getTraceId() || this.traceId;
  }

  logPageView(customProperties?: { [key: string]: any }): void {
    this.logPageViewAsync(customProperties).then(() => { }).catch(console.error);
  }

  private async logPageViewAsync(customProperties?: { [key: string]: any }): Promise<void> {
    await this.ensureInitialized();

    const telemetry: IPageViewTelemetry = {};
    telemetry.name = this.router.url;
    telemetry.properties = customProperties || {};
    this.applyOperationContext(telemetry.properties);
    this.applyDiagnostics(telemetry.properties);
    this.applyUserInfo(telemetry.properties);

    this.appInsights?.trackPageView(telemetry);
  }

  private async logEventAsync(eventName: string, customProperties?: { [key: string]: any }): Promise<void> {
    await this.ensureInitialized();

    customProperties = customProperties || {};
    this.applyOperationContext(customProperties);
    this.applyDiagnostics(customProperties);
    this.applyUserInfo(customProperties);

    const event: IEventTelemetry = { name: eventName };
    this.appInsights?.trackEvent(event, customProperties);
  }

  logCustomEvent(eventName: string, customProperties?: { [key: string]: any }): void {
    customProperties = customProperties || {};
    this.logEventAsync(eventName, customProperties)
      .then(() => { }).catch(console.error);
  }

  logCompleted(actionName: string, customProperties?: { [key: string]: any }): void {
    customProperties = customProperties || {};
    customProperties.SOActionName = actionName;
    this.logEventAsync("ActionCompleted", customProperties)
      .then(() => { }).catch(console.error);
  }

  logFailed(actionName: string, error: any): void {
    this.logEventAsync("ActionFailed", {
      SOActionName: actionName,
      SOError: error
    })
      .then(() => { }).catch(console.error);
  }

  logError(error: any): void {
    this.logErrorAsync(error).then(() => { }).catch(console.error);
  }

  logMetric(name: string, value: number): void {
    this.logMetricAsync(name, value).then(() => { }).catch(console.error);
  }

  private async logMetricAsync(name: string, value: number): Promise<void> {
    await this.ensureInitialized();
    this.appInsights?.trackMetric({ name, average: value });
  }

  logTrace(message: string, customProperties?: { [key: string]: any }): void {
    this.logTraceAsync(message, customProperties).then(() => { }).catch(console.error);
  }

  private async logTraceAsync(message: string, customProperties?: { [key: string]: any }): Promise<void> {
    await this.ensureInitialized();

    customProperties = customProperties || {};
    this.applyOperationContext(customProperties);
    this.applyDiagnostics(customProperties);
    this.applyUserInfo(customProperties);

    const telemetry: ITraceTelemetry = {
      message,
      properties: customProperties,
      severityLevel: SeverityLevel.Information,
    };

    this.appInsights?.trackTrace(telemetry);
  }

  private async logErrorAsync(error: any): Promise<void> {
    await this.ensureInitialized();

    const properties = {};
    this.applyOperationContext(properties);
    this.applyDiagnostics(properties);
    this.applyUserInfo(properties);

    const exception = <IExceptionTelemetry>{
      exception: error,
      properties,
      severityLevel: SeverityLevel.Error
    };

    this.appInsights?.trackException(exception);
  }

  logCancelled(actionName: string): void {
    this.logEventAsync("ActionCancelled", { SOActionName: actionName })
      .then(() => { }).catch(console.error);
  }

  private applyUserInfo(props: { [key: string]: any }) {
    try {
      props.SOApi = this.userInfo.api;
      props.SOAssociateId = this.userInfo.associate_id;
      props.SOCompanyName = this.userInfo.company_name;
      props.SOSecureId = this.userInfo.id;
      props.SOInitials = this.userInfo.initials;
      props.SOMailLinkApi = this.userInfo.maillink_api;
      props.SOTenant = this.userInfo.tenant;
      props.SOUser = this.userInfo.user;
    } catch { }
  }

  private applyDiagnostics(props: { [key: string]: any }) {
    try {
      props.OutlookHost = Office.context.mailbox.diagnostics.hostName;
      props.OutlookVersion = Office.context.mailbox.diagnostics.hostVersion;
    } catch { }
  }

  private applyOperationContext(props: { [key: string]: any }) {
    props.SOUiRoute = this.router.url;
  }

  // once user is logged in, we want to enrich app-insights data to assist
  // better tracing, with more contextual data.
  updateUserInfo(): void {
    this.updateUserInfoAsync().then(() => { }).catch(console.error);
  }

  private async updateUserInfoAsync(): Promise<void> {
    await this.ensureInitialized();
    this.appInsights?.setAuthenticatedUserContext(
      this.userInfo.user, this.userInfo.tenant, true
    );
  }

  private async ensureInitialized() {
    if (!this.isInitialized) {
      await this.initializationPromise;
    }
  }
}
